import * as React from 'react';
import { Icon } from 'sos-common/js/components/Icon/icon';
import { TextInputForPrint } from 'sos-common/js/components/FormControls/TextInputForPrint';
import { Field } from '../../StatsbidragGlobals/statsbidrag.types';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import _ from 'lodash';
import {
  calculationComponentProps,
  calculationComponentState,
  calculationComponentModel,
  calculationComponentModel2,
  ColumnTypeGroup,
  ColumnValue,
  Header,
} from '../calculationComponents.types';
import {
  serializeComponent,
  updateField,
  getInitialFieldsFromJsonData,
  uuidv4,
  valueObject,
  getSerializedXmlInputComponent,
  IJsonDataStuctures,
} from '../calculationComponents.helpers';
import EventService from '../calculationComponents.eventservice';
import ValidationService from '../../../services/validation.service';
import { InputError } from 'sos-common/js/components/InputError/InputError';
import { InputInfo } from 'sos-common/js/components/InputInfo/inputInfo';
import { hashCode } from 'sos-common/js/utils/hash';

const MAX_DYNAMIC_GROUPS: number = 20;

export interface ActivityGoalDescriptionProps extends calculationComponentProps {
  goalLabel: string;
  goalHeading: string;
  goalHeadingPlaceholder: string;
  goalDescription: string;
  goalDescriptionPlaceholder: string;
  componentId: string;
  addMoreLabel: string;
  disableAddMore: boolean;
  preDefinedRows: activityGoalDescriptionListCell[];
}

export interface ActivityGoalDescriptionState extends calculationComponentState {
  fields: Field[][];
  jsonXmlInput: string;
}

export interface activityGoalDescriptionListCell {
  GoalDescription: string;
  GoalDescriptionPlaceholder: string;
}

export class ActivityGoalDescription extends React.Component<
  ActivityGoalDescriptionProps,
  ActivityGoalDescriptionState
> {
  private _fieldCount: number;
  private _errorId: string;

  constructor(props: ActivityGoalDescriptionProps) {
    super(props);
    this._fieldCount = 0;
    this._errorId = hashCode(props.componentId, 'error');
    let initialFields: Field[][] = [];

    initialFields = [this.getDefaultEmptyFieldRow()];

    let isValid = true;

    if (props.formElementJson) {
      try {
        let calculationComponentModel = JSON.parse(props.formElementJson) as calculationComponentModel;
        initialFields = getInitialFieldsFromJsonData(
          calculationComponentModel,
          initialFields,
          this.getDefaultEmptyFieldRow
        );
        initialFields.forEach((field) => {
          if (field[1].value.trim() == '') {
            /* NOTE: FOET-100 backward compatibility fix in deploy: 
             If there are saved fields that lack ID before release which was the problem in FOET-100 bug, these ID's must be added. 
             This could happen in a deploy scenario if there are users that have saved data in a form, but that have not yet submitted the data. 
             So it solves the backward compatibility issue during this release. */
            field[1].value = uuidv4();
          }
        });

        let validation = ValidationService.validateComponent(initialFields, props.formElementName);
        isValid = validation.componentIsValid;
        initialFields = validation.fieldsClone;
      } catch (error) {
        console.warn(error);
      }
    }

    this.state = {
      fields: initialFields,
      jsonFields: '',
      isValid,
      isTouched: false,
      jsonXmlInput: '',
    };
  }

  valuesArePrePopulated = (array) => {
    return (
      array
        .slice(2, array.length)
        .map((x) => x.value)
        .filter((x) => x.length !== 0).length > 0
    );
  };

  componentDidMount = () => {
    const { componentId, goalLabel } = this.props;
    this.updateJsonFields();
    this.broadcastGoals();
    EventService.fire(`ActivityGoalLabel:${componentId}`, { detail: goalLabel });
    EventService.subscribe('form-is-valid', (e: CustomEvent) => {
      this.setState({ isTouched: true });
    });
  };

  validateMe = () => {
    let isValid = true;

    // Validate standard field logic
    let validation = ValidationService.validateComponent(this.state.fields, this.props.formElementName);
    isValid = validation.componentIsValid;
    let fields = validation.fieldsClone;

    this.setState({ fields, isValid }, () => {
      ValidationService.validateStepViaElementName(this.props.formElementName);
    });
  };

  updateJsonFields = () => {
    const { formElementName, formElementHeading } = this.props;
    const { fields } = this.state;

    this.setState(
      {
        jsonFields: this.getSerializedComponent(fields),
        jsonXmlInput: getSerializedXmlInputComponent(
          this.state.fields,
          calculationComponentModel2.Create(formElementName, formElementHeading, 'Verksamhetsmål'),
          this.jsonSeriliazetoMainContentForXmlInput
        ),
      },
      () => {
        this.validateMe();
      }
    );
  };

  jsonSeriliazetoMainContentForXmlInput = (fields: Field[][], data: IJsonDataStuctures) => {
    if (fields.length == 0) throw 'At least one field must exist for this component ActivityGoalDescription';
    const atLeastThreeFieldsMessage =
      'At least three inner field must exist for this component ActivityGoalDescription.';
    const innerField = fields[0];
    if (innerField.length < 3) throw atLeastThreeFieldsMessage;
    for (let ix = 2; ix < innerField.length; ix++) {
      const headerId = ix - 1;
      var header = { id: headerId, value: innerField[ix].label } as Header;
      data.headerDefinitions.push(header);
    }

    fields.forEach((field) => {
      if (field.length < 3) throw atLeastThreeFieldsMessage;

      const group = { headerValue: field[0].value, columnValues: [] } as ColumnTypeGroup;
      for (let ix = 2; ix < field.length; ix++) {
        const headerId = ix - 1;
        const columnValue = { headerId, value: field[ix].value } as ColumnValue;
        group.columnValues.push(columnValue);
      }
      data.columnTypeGroups.push(group);
    });
  };

  getSerializedComponent = (fields: Field[][]) => {
    const { goalHeading, goalDescription, formElementHeading } = this.props;
    let values = [];
    fields.forEach((field, i) => {
      values.push(field.map((x) => valueObject(x.value, x.type !== 'hidden')));
      valueObject(values, false);
    });
    if (values.length > 0) {
      return serializeComponent([goalHeading, goalDescription], values, null, formElementHeading);
    }
    return '';
  };

  generateFieldRowId = () => {
    return `activitygoaldesc__${this._fieldCount++}`;
  };

  getDefaultEmptyFieldRow = () => {
    const { goalHeading, goalHeadingPlaceholder } = this.props;

    let fieldRow = [
      {
        id: this.generateFieldRowId(),
        type: 'hidden',
        name: this.generateFieldRowId(),
        placeholder: '',
        mandatory: false,
        value: '',
        isValid: true,
      },
      {
        id: this.generateFieldRowId(),
        type: 'hidden',
        name: this.generateFieldRowId(),
        placeholder: '',
        mandatory: false,
        value: uuidv4(), //NOTE FOET-100: This gives us a unique identifier that we always save to database and can match against.
        isValid: true,
      },
      {
        id: this.generateFieldRowId(),
        name: this.generateFieldRowId(),
        type: 'text',
        value: '',
        placeholder: goalHeadingPlaceholder,
        isValid: true,
        mandatory: true,
        isReadOnly: false,
        label: goalHeading,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (goalHeading ? goalHeading.toLowerCase() : 'denna uppgift'),
      },
    ] as Field[];

    this.props.preDefinedRows.forEach((x) =>
      fieldRow.push({
        id: this.generateFieldRowId(),
        type: 'text',
        name: this.generateFieldRowId(),
        value: '',
        placeholder: x.GoalDescriptionPlaceholder,
        isValid: true,
        mandatory: true,
        label: x.GoalDescription,
        isTouched: false,
        errorMessage: 'Du måste ange ' + (x.GoalDescription ? x.GoalDescription.toLowerCase() : 'denna uppgift'),
      })
    );

    return fieldRow;
  };

  setValue = (field: FormField<any>) => {
    const fieldsClone = this.state.fields.slice();
    const fieldToUpdate = updateField(field, fieldsClone);
    fieldsClone.forEach((fieldArr, index) => {
      fieldArr[0].value = `${this.props.goalLabel} ${index + 1}`;
    });

    this.setState({ fields: fieldsClone /*isTouched: true*/ }, () => {
      this.updateJsonFields();
    });
  };

  renderRows = (row: Field[], index: number) => {
    this.setAriaErrorObjects(row);

    return (
      <div key={`activitygoaldesc__${row[0].name}`} className="salaryexp__group form-group-small">
        <h3>
          {this.props.goalLabel} {index + 1}
        </h3>
        <input type="hidden" value={`${this.props.goalLabel} ${index + 1}`} id={row[0].id} />
        <input type="hidden" value={row[1].value} id={row[1].id} />
        <div className="form-group-small">
          <label htmlFor={row[2].id}>{row[2].label}</label>
          <TextInputForPrint
            id={row[2].id}
            placeholder={row[2].placeholder}
            value={row[2].value}
            onChange={this.setValue}
            fieldName={row[2].name}
            type={row[2].type}
            classNames={this.shouldShowError(row[2]) ? 'has-error' : ''}
            isRequired={true}
            onBlur={this.onBlur}
            errorAriaAttributes={row[2].errorAriaAttributes}
            changeOnBlur={false}
          />
          {this.renderError(row[2])}
        </div>

        {row.map(
          (x, i) =>
            i >= 3 && (
              <div key={`form-group-small__${i}`} className="form-group-small">
                <label htmlFor={x.id}>{x.label}</label>
                <TextInputForPrint
                  id={x.id}
                  placeholder={x.placeholder}
                  value={x.value}
                  onChange={this.setValue}
                  fieldName={x.name}
                  type={x.type}
                  multiline={true}
                  maxLength={20000}
                  classNames={`big-text-area ${this.shouldShowError(x) ? 'has-error' : ''}`}
                  isRequired={true}
                  onBlur={this.onBlur}
                  errorAriaAttributes={x.errorAriaAttributes}
                  changeOnBlur={false}
                />
                {this.renderError(x)}
              </div>
            )
        )}

        {this.state.fields.length > 1 && (
          <div className="delete">
            <button type="button" onClick={(e) => this.onRemoveGroup(e, index)} className="stripped-button">
              Ta bort <Icon iconType="Paperbin" />
            </button>
          </div>
        )}
      </div>
    );
  };

  renderError = (field: Field) => {
    if (this.shouldShowError(field) && field.errorAriaAttributes) {
      return (
        <InputError
          key={`${field.id}_cError`}
          id={field.errorAriaAttributes['aria-describedby']}
          errorText={field.errorMessage}
        />
      );
    }
  };

  setAriaErrorObjects = (fieldRow: Field[]) => {
    fieldRow.forEach((field) => {
      field.errorAriaAttributes = null;
      if (this.shouldShowError(field)) {
        field.errorAriaAttributes = { 'aria-invalid': true, 'aria-describedby': uuidv4() };
      }
    });
  };

  shouldShowError = (field: Field) => {
    return (this.state.isTouched || field.isTouched) && !field.isValid;
  };

  onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { fields } = this.state;
    const clone = fields.slice();
    let targetField: Field = null;
    clone.forEach((field) => {
      let foundField = field.find((f) => f.id === e.target.id);
      if (foundField) targetField = foundField;
    });
    if (targetField) {
      targetField.isTouched = true;
      this.setState({ fields: clone });
    }
  };

  broadcastGoals = () => {
    const identifiers = [];
    this.state.fields.forEach((field) => identifiers.push(field[1].value));
    EventService.fire(`ActivityGoalModel:${this.props.componentId}`, { detail: identifiers });
  };

  onRemoveGroup = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    const fieldsClone = this.state.fields.slice();
    fieldsClone.splice(index, 1);
    this.setState({ fields: fieldsClone }, () => {
      this.updateJsonFields();
      this.broadcastGoals();
    });
  };

  onAddMore = (e: React.MouseEvent) => {
    e.preventDefault();
    const fieldsClone = this.state.fields.slice();
    const addedRow = this.getDefaultEmptyFieldRow();
    fieldsClone.push(addedRow);
    this.setState({ fields: fieldsClone }, () => {
      const fieldToFocus = document.getElementById(addedRow[1].id);
      if (fieldToFocus) fieldToFocus.focus();
      this.broadcastGoals();
      this.updateJsonFields();
    });
  };

  public render() {
    const { disableAddMore, addMoreLabel } = this.props;
    const { fields } = this.state;
    return (
      <div className="cc-component">
        {fields && fields.map(this.renderRows)}
        {!disableAddMore && fields.length < MAX_DYNAMIC_GROUPS && (
          <div className="autosum__btnwrap">
            <button type="button" className="autosum__add" onClick={this.onAddMore}>
              {addMoreLabel} <Icon iconType="baseline-add_circle_outline-24px" />
            </button>
          </div>
        )}
        {fields.length >= MAX_DYNAMIC_GROUPS && (
          <div className="autosum__btnwrap">
            <InputInfo
              infoText={`Du kan inte lägga till fler än ${MAX_DYNAMIC_GROUPS} ${this.props.goalLabel.toLowerCase()}`}
            />
          </div>
        )}
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={this.props.formElementName}
          id={this.props.formElementName}
          value={this.state.jsonFields}
        />
        <input
          data-isvalid={this.state.isValid}
          type="hidden"
          name={'__XmlInput' + this.props.formElementName}
          id={'__XmlInput' + this.props.formElementName}
          value={this.state.jsonXmlInput}
        />
      </div>
    );
  }
}
